import { BehaviorDescriptor, type PropType } from "@loumy/contracts";

// D75 — Built-in-Behavior-Bibliothek des 2D-Platformers (Tier 3). NUR Descriptoren + Gizmo-Hints,
// KEIN Runtime-Code (der ist Engine-Sache, D73): die Engine-Runtime bindet diese Built-in-ids an ihre
// eigenen Standard-Implementierungen. Jeder Eintrag ist `source:"builtin"` und trägt KEINE `binding`
// (der ManifestLoader erzwingt das fail-closed). Prop-Schemata sind sinnvolle Startwerte und können
// kalibriert werden (Editor_PRD §16).
//
// D102 — Built-ins deklarieren KEINE `states`: der Editor erfindet keine Zustände („Schalter an"/
// „Tür offen"). States kommen ausschließlich aus dem Projekt-CODE (defineBehavior → Extractor →
// Manifest, D70/D73); `BuiltinInput` hat deshalb bewusst kein `states`-Feld (strukturell erzwungen).

type PropInput = { type: PropType; label?: string; description?: string };
interface BuiltinInput {
  id: string;
  label: string;
  category: string;
  props?: Record<string, PropInput>;
  events?: string[];
  actions?: Array<{ id: string; label?: string; params?: Record<string, PropInput> }>;
  gizmo?: string;
}

/** Validiert + füllt Defaults über den SSOT-Vertrag (D70) und pinnt `source:"builtin"`. */
function builtin(b: BuiltinInput): BehaviorDescriptor {
  return BehaviorDescriptor.parse({
    id: b.id,
    label: b.label,
    category: b.category,
    props: b.props ?? {},
    states: [], // D102 — nie editor-definierte States
    events: b.events ?? [],
    actions: b.actions ?? [],
    editor: b.gizmo ? { gizmo: b.gizmo } : undefined,
    source: "builtin",
  });
}

const num = (def: number, extra: Partial<{ min: number; max: number; step: number; unit: string }> = {}): PropType => ({
  kind: "number",
  default: def,
  ...extra,
});
const int = (def: number, extra: Partial<{ min: number; max: number }> = {}): PropType => ({ kind: "int", default: def, ...extra });
const bool = (def: boolean): PropType => ({ kind: "bool", default: def });
const vec2List: PropType = { kind: "list", of: { kind: "vec2" } };

// ── Geometrie / Kollision ────────────────────────────────────────────────────
const solid = builtin({ id: "solid", label: "Solid", category: "geometry" });
const oneWayPlatform = builtin({
  id: "one-way-platform",
  label: "Einweg-Plattform",
  category: "geometry",
  props: { direction: { type: { kind: "enum", options: ["up", "down"], default: "up" }, label: "Durchlass-Richtung" } },
});
const slippery = builtin({
  id: "slippery",
  label: "Rutschig",
  category: "geometry",
  props: { friction: { type: num(0.02, { min: 0, max: 1, step: 0.01 }), label: "Reibung" } },
});
const hazard = builtin({
  id: "hazard",
  label: "Gefahr",
  category: "geometry",
  props: { damage: { type: int(1, { min: 0 }), label: "Schaden" } },
  events: ["touched"],
  actions: [{ id: "enable" }, { id: "disable" }],
});
const ladder = builtin({ id: "ladder", label: "Leiter", category: "geometry" });
const water = builtin({
  id: "water",
  label: "Wasser",
  category: "geometry",
  props: { buoyancy: { type: num(0.5, { min: 0, max: 2, step: 0.1 }), label: "Auftrieb" } },
});

// ── Bewegung ──────────────────────────────────────────────────────────────────
const moveablePlatform = builtin({
  id: "moveable-platform",
  label: "Bewegliche Plattform",
  category: "movement",
  props: {
    speed: { type: num(50, { min: 0, unit: "px/s" }), label: "Geschwindigkeit" },
    path: { type: vec2List, label: "Pfad" },
    loop: { type: bool(true), label: "Schleife" },
  },
  events: ["reached-end", "reached-start"],
  actions: [{ id: "start" }, { id: "stop" }, { id: "reverse" }],
  gizmo: "path",
});
const conveyor = builtin({
  id: "conveyor",
  label: "Förderband",
  category: "movement",
  props: {
    speed: { type: num(30, { min: 0, unit: "px/s" }), label: "Geschwindigkeit" },
    direction: { type: { kind: "enum", options: ["left", "right"], default: "right" }, label: "Richtung" },
  },
});
const falling = builtin({
  id: "falling",
  label: "Bröckelnde Plattform",
  category: "movement",
  props: {
    delay: { type: num(0.5, { min: 0, unit: "s" }), label: "Verzögerung" },
    respawn: { type: bool(true), label: "Respawn" },
  },
  events: ["fell"],
  actions: [{ id: "trigger" }],
});

// ── Interaktiv ──────────────────────────────────────────────────────────────────
const switchBehavior = builtin({
  id: "switch",
  label: "Schalter",
  category: "interactive",
  props: { startOn: { type: bool(false), label: "Startet an" } },
  events: ["toggled", "turned-on", "turned-off"],
  actions: [{ id: "toggle" }, { id: "turn-on" }, { id: "turn-off" }],
});
const door = builtin({
  id: "door",
  label: "Tür",
  category: "interactive",
  props: { startOpen: { type: bool(false), label: "Startet offen" } },
  events: ["opened", "closed"],
  actions: [{ id: "open" }, { id: "close" }, { id: "toggle" }],
});
const lever = builtin({
  id: "lever",
  label: "Hebel",
  category: "interactive",
  events: ["pulled"],
  actions: [{ id: "pull" }, { id: "reset" }],
});
const button = builtin({
  id: "button",
  label: "Knopf",
  category: "interactive",
  props: { momentary: { type: bool(true), label: "Tastend" } },
  events: ["pressed", "released"],
  actions: [{ id: "press" }, { id: "release" }],
});

// ── Trigger / Flow ──────────────────────────────────────────────────────────────
const collectible = builtin({
  id: "collectible",
  label: "Sammelobjekt",
  category: "trigger",
  props: { value: { type: int(1, { min: 0 }), label: "Wert" } },
  events: ["collected"],
  actions: [{ id: "collect" }],
});
const checkpoint = builtin({
  id: "checkpoint",
  label: "Kontrollpunkt",
  category: "trigger",
  events: ["reached"],
  actions: [{ id: "activate" }],
});
const spawn = builtin({
  id: "spawn",
  label: "Spawner",
  category: "trigger",
  props: {
    prototype: { type: { kind: "asset-ref" }, label: "Prototyp" },
    interval: { type: num(0, { min: 0, unit: "s" }), label: "Intervall" },
  },
  events: ["spawned"],
  actions: [{ id: "spawn-now" }],
});
const goal = builtin({ id: "goal", label: "Ziel", category: "trigger", events: ["reached"] });
const triggerZone = builtin({
  id: "trigger-zone",
  label: "Trigger-Zone",
  category: "trigger",
  props: { once: { type: bool(false), label: "Einmalig" } },
  events: ["entered", "exited"],
  gizmo: "region",
});

// ── Actor ──────────────────────────────────────────────────────────────────────
const playerSpawn = builtin({ id: "player-spawn", label: "Spieler-Start", category: "actor" });
const enemy = builtin({
  id: "enemy",
  label: "Gegner",
  category: "actor",
  props: {
    health: { type: int(1, { min: 0 }), label: "Leben" },
    speed: { type: num(40, { min: 0, unit: "px/s" }), label: "Geschwindigkeit" },
    patrol: { type: vec2List, label: "Patrouille" },
  },
  events: ["died", "hit"],
  actions: [{ id: "kill" }, { id: "hurt", params: { amount: { type: int(1, { min: 0 }) } } }],
  gizmo: "path",
});
const projectile = builtin({
  id: "projectile",
  label: "Projektil",
  category: "actor",
  props: {
    speed: { type: num(200, { min: 0, unit: "px/s" }), label: "Geschwindigkeit" },
    damage: { type: int(1, { min: 0 }), label: "Schaden" },
  },
  events: ["hit", "expired"],
  actions: [{ id: "fire" }],
});

/**
 * D71 — Platformer-globale Vokabel ("global"-Behavior): editor-typ-spezifische Events/Actions, die
 * NICHT an einer Entity hängen. Wird ins Logic-Vokabular eingespeist (LogicGraph behavior:"global").
 */
export const PLATFORMER_GLOBAL = builtin({
  id: "global",
  label: "Global",
  category: "global",
  events: ["on-start", "on-collide", "on-key", "on-timer"],
  actions: [
    { id: "goto-level", params: { name: { type: { kind: "string" } } } },
    { id: "spawn", params: { asset: { type: { kind: "asset-ref" } } } },
    { id: "destroy", params: { target: { type: { kind: "entity-ref" } } } },
    { id: "set-gravity", params: { value: { type: num(800, { unit: "px/s²" }) } } },
  ],
});

/** Alle Built-in-Behaviors des 2D-Platformers (inkl. der "global"-Vokabel). */
export const PLATFORMER_BUILTINS: BehaviorDescriptor[] = [
  solid,
  oneWayPlatform,
  slippery,
  hazard,
  ladder,
  water,
  moveablePlatform,
  conveyor,
  falling,
  switchBehavior,
  door,
  lever,
  button,
  collectible,
  checkpoint,
  spawn,
  goal,
  triggerZone,
  playerSpawn,
  enemy,
  projectile,
  PLATFORMER_GLOBAL,
];
